/**
 * Four JSON-config bundle editors that hang off the contractor's
 * org settings page (PlanBillingPage's BrandingDefaultsForm). Each
 * one PATCHes the same /organizations/{id}/branding endpoint
 * with its own slice; they don't share dirty state because the
 * config domains are independent (a contractor might tweak their
 * payment-terms rules without touching brand prefs).
 *
 * Bundles:
 *
 *   1. Payment-terms rules — threshold-based payment schedules
 *      (deposit / draws / completion) by quote total.
 *   2. Brand-spec defaults — per-category preferred brand for AI
 *      to use in line descriptions.
 *   3. Default disclaimers — boilerplate Discovery Items / Material
 *      Volatility text appended to every new quote.
 *   4. Payment methods — accepted payment rails on the invoice
 *      block.
 */
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { useUpdateOrgBranding } from "@/api/hooks";
import type {
  OrganizationDetail,
  OrgBrandPreference,
  OrgDisclaimer,
  OrgPaymentMethod,
  OrgPaymentTermsRule,
} from "@/api/types";
import { formatCents } from "@/lib/format";

// ---------------------------------------------------------------
// Top-level
// ---------------------------------------------------------------

export function OrgConfigBundles({
  orgId,
  org,
}: {
  orgId: string;
  org: OrganizationDetail | null;
}) {
  if (!org) return null;
  return (
    <div className="space-y-6">
      <PaymentTermsRulesEditor orgId={orgId} initial={org.payment_terms_rules} />
      <BrandPreferencesEditor orgId={orgId} initial={org.brand_preferences} />
      <DefaultDisclaimersEditor orgId={orgId} initial={org.default_disclaimers} />
      <PaymentMethodsEditor orgId={orgId} initial={org.payment_methods} />
    </div>
  );
}

// ---------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------

function SectionShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-rule pt-5">
      <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
        {title}
      </h3>
      <p className="mt-1 max-w-prose text-xs text-graphite-soft">{description}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function SaveBar({
  dirty,
  saving,
  onSave,
  flash,
  error,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  flash: boolean;
  error: string | null;
}) {
  return (
    <div className="mt-3 flex items-center justify-end gap-3">
      {error && <span className="font-mono text-xs text-red-pencil">{error}</span>}
      {flash && (
        <span className="font-mono text-xs text-graphite-soft">Saved.</span>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// Trivial JSON-deep-equal for dirty checks. The configs are short
// and the comparison runs on every input change; keep it simple.
function jsonEq<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---------------------------------------------------------------
// 1. Payment-terms rules
// ---------------------------------------------------------------

function PaymentTermsRulesEditor({
  orgId,
  initial,
}: {
  orgId: string;
  initial: OrgPaymentTermsRule[] | null;
}) {
  const update = useUpdateOrgBranding(orgId);
  const [rules, setRules] = useState<OrgPaymentTermsRule[]>(initial ?? []);
  const [serverState, setServerState] = useState<OrgPaymentTermsRule[]>(
    initial ?? [],
  );
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRules(initial ?? []);
    setServerState(initial ?? []);
  }, [initial]);

  const dirty = !jsonEq(rules, serverState);

  function addRule() {
    setRules((prev) => [
      ...prev,
      {
        min_total_cents: 0,
        max_total_cents: null,
        schedule: [
          { label: "Deposit", percent: 0.5, trigger: "on signing" },
          { label: "Completion", percent: 0.5, trigger: "on completion" },
        ],
      },
    ]);
  }

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({ payment_terms_rules: rules });
      setServerState(rules);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not save.");
    }
  }

  return (
    <SectionShell
      title="Payment-terms rules"
      description="Map quote totals to a payment schedule (deposit / draws / completion). The first rule whose [min, max] window contains the quote total is used. Empty list = no auto-suggested schedule."
    >
      {rules.length === 0 ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No rules yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {rules.map((rule, ruleIdx) => (
            <li
              key={ruleIdx}
              className="rounded-lg border border-rule bg-paper-warm p-3 space-y-2"
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
                    Min total ($)
                  </span>
                  <input
                    type="number"
                    value={rule.min_total_cents != null ? rule.min_total_cents / 100 : ""}
                    onChange={(e) =>
                      setRules((prev) =>
                        prev.map((r, i) =>
                          i === ruleIdx
                            ? {
                                ...r,
                                min_total_cents:
                                  e.target.value === ""
                                    ? null
                                    : Math.round(Number(e.target.value) * 100),
                              }
                            : r,
                        ),
                      )
                    }
                    placeholder="0 = open-ended"
                    className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
                    Max total ($)
                  </span>
                  <input
                    type="number"
                    value={rule.max_total_cents != null ? rule.max_total_cents / 100 : ""}
                    onChange={(e) =>
                      setRules((prev) =>
                        prev.map((r, i) =>
                          i === ruleIdx
                            ? {
                                ...r,
                                max_total_cents:
                                  e.target.value === ""
                                    ? null
                                    : Math.round(Number(e.target.value) * 100),
                              }
                            : r,
                        ),
                      )
                    }
                    placeholder="empty = open-ended"
                    className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums"
                  />
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setRules((prev) => prev.filter((_, i) => i !== ruleIdx))
                  }
                  className="self-end rounded border border-rule bg-paper p-2 text-graphite-soft hover:text-red-pencil"
                  title="Delete rule"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>

              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
                  Schedule
                </span>
                <ul className="mt-1 space-y-1.5">
                  {rule.schedule.map((entry, entryIdx) => (
                    <li
                      key={entryIdx}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[2fr_80px_2fr_auto]"
                    >
                      <input
                        type="text"
                        value={entry.label}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((r, i) =>
                              i === ruleIdx
                                ? {
                                    ...r,
                                    schedule: r.schedule.map((s, j) =>
                                      j === entryIdx
                                        ? { ...s, label: e.target.value }
                                        : s,
                                    ),
                                  }
                                : r,
                            ),
                          )
                        }
                        placeholder="Deposit"
                        className="rounded border border-rule bg-paper px-2 py-1 text-sm"
                      />
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={Math.round(entry.percent * 100)}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((r, i) =>
                              i === ruleIdx
                                ? {
                                    ...r,
                                    schedule: r.schedule.map((s, j) =>
                                      j === entryIdx
                                        ? {
                                            ...s,
                                            percent: Number(e.target.value) / 100,
                                          }
                                        : s,
                                    ),
                                  }
                                : r,
                            ),
                          )
                        }
                        className="rounded border border-rule bg-paper px-2 py-1 text-right font-mono text-sm tabular-nums"
                      />
                      <input
                        type="text"
                        value={entry.trigger ?? ""}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((r, i) =>
                              i === ruleIdx
                                ? {
                                    ...r,
                                    schedule: r.schedule.map((s, j) =>
                                      j === entryIdx
                                        ? { ...s, trigger: e.target.value }
                                        : s,
                                    ),
                                  }
                                : r,
                            ),
                          )
                        }
                        placeholder="on signing / on completion"
                        className="rounded border border-rule bg-paper px-2 py-1 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setRules((prev) =>
                            prev.map((r, i) =>
                              i === ruleIdx
                                ? {
                                    ...r,
                                    schedule: r.schedule.filter(
                                      (_, j) => j !== entryIdx,
                                    ),
                                  }
                                : r,
                            ),
                          )
                        }
                        className="rounded border border-rule bg-paper p-1.5 text-graphite-soft hover:text-red-pencil"
                        title="Delete schedule entry"
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() =>
                    setRules((prev) =>
                      prev.map((r, i) =>
                        i === ruleIdx
                          ? {
                              ...r,
                              schedule: [
                                ...r.schedule,
                                { label: "Payment", percent: 0.0, trigger: "" },
                              ],
                            }
                          : r,
                      ),
                    )
                  }
                  className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-graphite-soft hover:text-graphite"
                >
                  <Plus className="h-3 w-3" aria-hidden /> Add schedule entry
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={addRule}
        className="mt-3 inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add rule
      </button>

      <SaveBar
        dirty={dirty}
        saving={update.isPending}
        onSave={save}
        flash={flash}
        error={error}
      />
    </SectionShell>
  );
}

// ---------------------------------------------------------------
// 2. Brand preferences
// ---------------------------------------------------------------

function BrandPreferencesEditor({
  orgId,
  initial,
}: {
  orgId: string;
  initial: OrgBrandPreference[] | null;
}) {
  const update = useUpdateOrgBranding(orgId);
  const [prefs, setPrefs] = useState<OrgBrandPreference[]>(initial ?? []);
  const [serverState, setServerState] = useState<OrgBrandPreference[]>(initial ?? []);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPrefs(initial ?? []);
    setServerState(initial ?? []);
  }, [initial]);

  const dirty = !jsonEq(prefs, serverState);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({ brand_preferences: prefs });
      setServerState(prefs);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not save.");
    }
  }

  return (
    <SectionShell
      title="Brand-spec defaults"
      description="When a line's category matches one of these, the AI fills in the brand + model in the description ('Moen Tetra (or comparable)'). Empty list = generic descriptions."
    >
      {prefs.length === 0 ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No brand defaults yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {prefs.map((pref, idx) => (
            <li
              key={idx}
              className="grid grid-cols-1 gap-2 rounded border border-rule bg-paper-warm p-2 sm:grid-cols-[1fr_1fr_1fr_120px_120px_auto]"
            >
              <input
                type="text"
                placeholder="Category (e.g. faucets)"
                value={pref.category}
                onChange={(e) =>
                  setPrefs((prev) =>
                    prev.map((p, i) =>
                      i === idx ? { ...p, category: e.target.value } : p,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              />
              <input
                type="text"
                placeholder="Brand (Moen, Kohler…)"
                value={pref.brand}
                onChange={(e) =>
                  setPrefs((prev) =>
                    prev.map((p, i) =>
                      i === idx ? { ...p, brand: e.target.value } : p,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              />
              <input
                type="text"
                placeholder="Model (Tetra)"
                value={pref.model ?? ""}
                onChange={(e) =>
                  setPrefs((prev) =>
                    prev.map((p, i) =>
                      i === idx
                        ? { ...p, model: e.target.value.trim() || null }
                        : p,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              />
              <select
                value={pref.tier ?? ""}
                onChange={(e) =>
                  setPrefs((prev) =>
                    prev.map((p, i) =>
                      i === idx
                        ? {
                            ...p,
                            tier:
                              (e.target.value as OrgBrandPreference["tier"]) ||
                              null,
                          }
                        : p,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              >
                <option value="">Tier (—)</option>
                <option value="budget">Budget</option>
                <option value="mid">Mid</option>
                <option value="premium">Premium</option>
              </select>
              <label className="inline-flex items-center gap-1.5 text-xs text-graphite-soft">
                <input
                  type="checkbox"
                  checked={pref.or_comparable}
                  onChange={(e) =>
                    setPrefs((prev) =>
                      prev.map((p, i) =>
                        i === idx
                          ? { ...p, or_comparable: e.target.checked }
                          : p,
                      ),
                    )
                  }
                />
                or comparable
              </label>
              <button
                type="button"
                onClick={() =>
                  setPrefs((prev) => prev.filter((_, i) => i !== idx))
                }
                className="rounded border border-rule bg-paper p-1.5 text-graphite-soft hover:text-red-pencil"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() =>
          setPrefs((prev) => [
            ...prev,
            {
              category: "",
              brand: "",
              model: null,
              tier: "mid",
              or_comparable: true,
            },
          ])
        }
        className="mt-3 inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add brand default
      </button>
      <SaveBar
        dirty={dirty}
        saving={update.isPending}
        onSave={save}
        flash={flash}
        error={error}
      />
    </SectionShell>
  );
}

// ---------------------------------------------------------------
// 3. Default disclaimers
// ---------------------------------------------------------------

function DefaultDisclaimersEditor({
  orgId,
  initial,
}: {
  orgId: string;
  initial: OrgDisclaimer[] | null;
}) {
  const update = useUpdateOrgBranding(orgId);
  const [items, setItems] = useState<OrgDisclaimer[]>(initial ?? []);
  const [serverState, setServerState] = useState<OrgDisclaimer[]>(initial ?? []);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(initial ?? []);
    setServerState(initial ?? []);
  }, [initial]);

  const dirty = !jsonEq(items, serverState);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({ default_disclaimers: items });
      setServerState(items);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not save.");
    }
  }

  return (
    <SectionShell
      title="Default disclaimers"
      description="Boilerplate text appended to every new quote (Discovery Items, Material Volatility, etc.). Toggle ``enabled`` to suppress one without deleting it."
    >
      {items.length === 0 ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No disclaimers configured.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="rounded-lg border border-rule bg-paper-warm p-3 space-y-2"
            >
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-xs text-graphite-soft">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((it, i) =>
                          i === idx
                            ? { ...it, enabled: e.target.checked }
                            : it,
                        ),
                      )
                    }
                  />
                  enabled
                </label>
                <input
                  type="text"
                  placeholder="Title"
                  value={item.title}
                  onChange={(e) =>
                    setItems((prev) =>
                      prev.map((it, i) =>
                        i === idx ? { ...it, title: e.target.value } : it,
                      ),
                    )
                  }
                  className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 text-sm font-semibold"
                />
                <input
                  type="text"
                  placeholder="key (slug)"
                  value={item.key}
                  onChange={(e) =>
                    setItems((prev) =>
                      prev.map((it, i) =>
                        i === idx ? { ...it, key: e.target.value } : it,
                      ),
                    )
                  }
                  className="w-40 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-xs"
                />
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                  className="rounded border border-rule bg-paper p-1.5 text-graphite-soft hover:text-red-pencil"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
              <textarea
                rows={3}
                placeholder="Body — what the homeowner reads on the public quote."
                value={item.body}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, i) =>
                      i === idx ? { ...it, body: e.target.value } : it,
                    ),
                  )
                }
                className="w-full rounded border border-rule bg-paper p-2 text-sm"
              />
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() =>
          setItems((prev) => [
            ...prev,
            {
              key: `disclaimer_${prev.length + 1}`,
              title: "",
              body: "",
              enabled: true,
            },
          ])
        }
        className="mt-3 inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add disclaimer
      </button>
      <SaveBar
        dirty={dirty}
        saving={update.isPending}
        onSave={save}
        flash={flash}
        error={error}
      />
    </SectionShell>
  );
}

// ---------------------------------------------------------------
// 4. Payment methods
// ---------------------------------------------------------------

function PaymentMethodsEditor({
  orgId,
  initial,
}: {
  orgId: string;
  initial: OrgPaymentMethod[] | null;
}) {
  const update = useUpdateOrgBranding(orgId);
  const [items, setItems] = useState<OrgPaymentMethod[]>(initial ?? []);
  const [serverState, setServerState] = useState<OrgPaymentMethod[]>(initial ?? []);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(initial ?? []);
    setServerState(initial ?? []);
  }, [initial]);

  const dirty = !jsonEq(items, serverState);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({ payment_methods: items });
      setServerState(items);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not save.");
    }
  }

  return (
    <SectionShell
      title="Payment methods"
      description="Accepted payment rails — renders on the public invoice. e.g. 'Check made out to Smith Remodeling LLC' / 'Zelle (704) 555-0100'."
    >
      {items.length === 0 ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No payment methods configured.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="grid grid-cols-1 gap-2 rounded border border-rule bg-paper-warm p-2 sm:grid-cols-[150px_1fr_1fr_auto]"
            >
              <select
                value={item.method}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, i) =>
                      i === idx ? { ...it, method: e.target.value } : it,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm"
              >
                <option value="check">Check</option>
                <option value="ach">ACH / e-Check</option>
                <option value="cash">Cash</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
                <option value="wire">Wire</option>
                <option value="cc">Credit card</option>
                <option value="other">Other</option>
              </select>
              <input
                type="text"
                placeholder="Payable to / handle / number"
                value={item.payable_to_or_handle ?? ""}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, i) =>
                      i === idx
                        ? {
                            ...it,
                            payable_to_or_handle:
                              e.target.value.trim() || null,
                          }
                        : it,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              />
              <input
                type="text"
                placeholder="Notes (optional)"
                value={item.notes ?? ""}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, i) =>
                      i === idx
                        ? { ...it, notes: e.target.value.trim() || null }
                        : it,
                    ),
                  )
                }
                className="rounded border border-rule bg-paper px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                className="rounded border border-rule bg-paper p-1.5 text-graphite-soft hover:text-red-pencil"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() =>
          setItems((prev) => [
            ...prev,
            {
              method: "check",
              payable_to_or_handle: null,
              notes: null,
            },
          ])
        }
        className="mt-3 inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add payment method
      </button>
      <SaveBar
        dirty={dirty}
        saving={update.isPending}
        onSave={save}
        flash={flash}
        error={error}
      />
    </SectionShell>
  );
}

// `formatCents` is imported above for potential future use (e.g.
// summarizing "Min total: $X" in a rule preview); keep the import
// stable to avoid unused-import churn during further iteration.
void formatCents;
